/**
 * 가상시계가 도는 동안 설비 수치를 주기적으로 다시 읽는다 (계획서 §13.2).
 *
 * WebSocket 의 `clock.updated` 는 시계만 갱신한다 (§12.6). 실제시간 1초마다 나가는
 * event 라 그때마다 전체 snapshot 을 다시 읽으면 요청이 과하기 때문이다.
 *
 * 그런데 설비 상태·생산량·온도·Hz·tank 는 가상시각이 흐르면 계속 바뀐다. 그래서
 * `clock.updated` 만 받는 동안에는 화면의 수치가 멈춰 있고, 다른 event 가 오거나
 * 사람이 새로고침해야 비로소 갱신됐다.
 *
 * 시계가 도는 동안에만 실제시간 2초 간격으로 snapshot 을 다시 읽어 이 간극을 메운다.
 * 정지 중에는 수치가 바뀌지 않으므로 폴링하지 않는다.
 */

import { useEffect } from "react";

import { useRunStore } from "./runStore";

/** 설비 수치 갱신 주기 (실제시간 ms). */
export const SNAPSHOT_POLL_INTERVAL_MS = 2_000;

/**
 * `runId` 의 snapshot 을 주기적으로 다시 읽는다.
 *
 * @param runId  구독할 run. `null` 이면 아무것도 하지 않는다.
 * @param active 가상시계가 도는 중인가. `false` 면 폴링하지 않는다.
 */
export function useSnapshotPolling(runId: string | null, active: boolean): void {
  const reload = useRunStore((s) => s.reload);

  useEffect(() => {
    if (!runId || !active) return;

    let cancelled = false;
    // 응답이 늦어도 요청이 겹치지 않도록 setInterval 이 아니라 이어붙인다.
    let timer = window.setTimeout(function tick() {
      void reload(runId)
        .catch(() => {
          // 일시적인 실패는 무시한다. WebSocket 재연결이 별도로 snapshot 을 복구한다.
        })
        .finally(() => {
          if (!cancelled) timer = window.setTimeout(tick, SNAPSHOT_POLL_INTERVAL_MS);
        });
    }, SNAPSHOT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [runId, active, reload]);
}
